import { CurrencyPipe, DecimalPipe } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { CONSTANTS, NumberUtils, RateUtils, StringUtils } from '@pk/powerkioskutils';
import * as XLSX from '@sheet/core';

import { LoadOptions } from 'devextreme/data';
import DxDataGrid from 'devextreme/ui/data_grid';
import _ from 'lodash';
import moment from 'moment-timezone';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Observable, Subject, switchMap, throttleTime } from 'rxjs';

import { MaskitoOptions } from '@maskito/core';
import { UsageVerificationType } from 'src/app/shared/models/usage-verification-type.enum';
import { environment } from '../../environments/environment';
import { GraphqlService } from '../graphql/graphql.service';
import { SecurityService } from '../security/security.service';
import { FeatureTourModalComponent } from './modals/feature-tour-modal/feature-tour-modal.component';
import {
	Contract,
	ContractMarketPerformance,
	Country,
	ReportCommissionProcessingIssuesRow,
	ReportContractStatus,
	UnpaidPayable,
	User,
	ViewContractLogChanges,
	ViewPayableForecastDashboard
} from './models';
import { Rate } from './models/rate';
import { WINDOW } from './models/window';
import { ServiceLocator } from './service-locator.service';

declare global {
	interface Window {
		opera: any;
	}

	/**
	 * Similar to `Partial<K>`, but object properties are also marked as `Partial`.
	 */
	type Subset<K> = {
		[attr in keyof K]?: K[attr] extends object ? Subset<K[attr]> : K[attr];
	};
}

@Injectable({
	providedIn: 'root',
})
export class HelperService {
	/* eslint-disable */
	public static phoneValidatorMap: { [key: number]: RegExp } = {
		[CONSTANTS.countries.unitedStates]: CONSTANTS.validators.phoneUS,
		[CONSTANTS.countries.canada]: CONSTANTS.validators.phoneUS,
		[CONSTANTS.countries.unitedKingdom]: CONSTANTS.validators.phoneGB,
		[CONSTANTS.countries.ireland]: CONSTANTS.validators.phoneIE,
		[CONSTANTS.countries.australia]: CONSTANTS.validators.phoneAU,
		[CONSTANTS.countries.singapore]: CONSTANTS.validators.phoneSG,
	};

	public static phoneMasksMap: { [key: number]: (string | RegExp)[] } = {
		[CONSTANTS.countries.unitedStates]: CONSTANTS.masks.phoneUS,
		[CONSTANTS.countries.canada]: CONSTANTS.masks.phoneUS,
		[CONSTANTS.countries.unitedKingdom]: CONSTANTS.masks.phoneGB,
		[CONSTANTS.countries.ireland]: CONSTANTS.masks.phoneIE,
		[CONSTANTS.countries.australia]: CONSTANTS.masks.phoneAU,
		[CONSTANTS.countries.singapore]: CONSTANTS.masks.phoneSG,
	};
	/* eslint-enable */

	public static rateSortTypes = [
		CONSTANTS.sort.ascDefault,
		CONSTANTS.sort.ascDisplayRate,
		CONSTANTS.sort.descResidualCommission,
		CONSTANTS.sort.descSavings,
		CONSTANTS.sort.descTerm,
		CONSTANTS.sort.ascTerm,
	];

	public static popularTerms = [6, 12, 18, 24, 36, 48, 60];

	constructor(
		@Inject(WINDOW) private window: Window,
		private securityService: SecurityService,
		private graphqlService: GraphqlService,
		private modalService: BsModalService,
	) { }

	/* eslint-disable */
	// http://detectmobilebrowsers.com
	public static isMobile(): boolean {
		let check = false;
		((a: string) => {
			if (
				/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
					a,
				) ||
				/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
					a.substr(0, 4),
				)
			) {
				check = true;
			}
		})(navigator.userAgent || navigator.vendor || window.opera);
		return check;
	}
	/* eslint-enable */

	public static isPossibleTest(input: { [key: string]: any }): boolean {
		if (environment.name !== 'production') {
			return false;
		}

		return Object.keys(input).some(
			key => typeof input[key] === 'string' && this.matches(input[key].trim(), /t+[^a-z]*e+[^a-z]*s+[^a-z]*t+/gi, false),
		);
	}

	public static matches(value: string, regExp: string | RegExp, isExact = true): boolean {
		const regExpMatchArray = value ? value.match(regExp) : null;
		return !!regExpMatchArray && !!regExpMatchArray.length && (!isExact || regExpMatchArray[0] === value);
	}

	public static serialize(obj: any): URLSearchParams {
		const params: URLSearchParams = new URLSearchParams();

		for (const key in obj) {
			if (obj.hasOwnProperty(key)) {
				const element = obj[key];
				if (element !== undefined && element !== 'undefined') {
					params.set(key, element);
				}
			}
		}
		return params;
	}

	public async getCalendlyOptions() {
		const companyId = this.securityService.authFields.loggedInUser.companyId || CONSTANTS.companies.powerKiosk;
		const result = await this.graphqlService.getCompanyCalendlySettingsData(companyId);
		return result.data.company.calendlySettings;
	}

	public static getProductMinBidAmount(productId: string, serviceTypeId: string, units: string): string {
		const factor = CONSTANTS.units[serviceTypeId]?.find(u => u.text === units)?.factor || 1;

		let minBid: number;

		switch (productId) {
			case CONSTANTS.products.fixedAllInElectric:
			case CONSTANTS.products.energyOnly:
			case CONSTANTS.products.fixedWithCapacityPassThrough:
			case CONSTANTS.products.congestionPassThrough: {
				minBid = 0.02;
				break;
			}
			case CONSTANTS.products.supplierFeeOnlyAllComponentsPassThrough:
			case CONSTANTS.products.indexPlus: {
				minBid = 0.0001;
				break;
			}
			case CONSTANTS.products.indexPlusExcludesCapacityEnergy: {
				minBid = 0.0;
				break;
			}
			case CONSTANTS.products.fixedAllInGas: {
				if (units.includes('kWh')) {
					// UK units
					minBid = 0.00198;
				} else {
					minBid = 0.1;
				}
				break;
			}
			case CONSTANTS.products.nymexPlus: {
				if (units.includes('kWh')) {
					// UK units
					minBid = 0.0;
				} else {
					minBid = 0.0;
				}
				break;
				break;
			}
			case CONSTANTS.products.indexAdderLocalHub: {
				minBid = 0.001;
				break;
			}
			case CONSTANTS.products.nymexAdder: {
				minBid = -0.4;
				break;
			}
			default: {
				minBid = 0.02;
				break;
			}
		}

		const decimalPipe = ServiceLocator.injector.get(DecimalPipe);
		return decimalPipe.transform(minBid * factor, '1.0-3');
	}

	public static getProductMaxBidAmount(productId: string, serviceTypeId: string, stateId: string, units: string): string {
		const factor = CONSTANTS.units[serviceTypeId]?.find(u => u.text === units)?.factor || 1;

		let maxBid: number;

		switch (productId) {
			case CONSTANTS.products.fixedAllInElectric:
			case CONSTANTS.products.energyOnly:
			case CONSTANTS.products.fixedWithCapacityPassThrough:
			case CONSTANTS.products.congestionPassThrough: {
				maxBid = 0.3;
				break;
			}
			case CONSTANTS.products.supplierFeeOnlyAllComponentsPassThrough:
			case CONSTANTS.products.indexPlus: {
				maxBid = 0.08;
				if (stateId === CONSTANTS.states.newJersey) {
					maxBid = 0.1;
				}
				break;
			}
			case CONSTANTS.products.fixedAllInGas:
			case CONSTANTS.products.nymexPlus: {
				if (units.includes('kWh')) {
					// UK units
					maxBid = 0.3;
				} else {
					maxBid = 2.5;
				}
				break;
			}
			case CONSTANTS.products.indexAdderLocalHub: {
				maxBid = 0.5;
				break;
			}
			case CONSTANTS.products.nymexAdder: {
				maxBid = 0.5;
				if (stateId === CONSTANTS.states.newHampshire) {
					maxBid = 0.65;
				}
				break;
			}
			default: {
				maxBid = 0.3;
				break;
			}
		}

		const decimalPipe = ServiceLocator.injector.get(DecimalPipe);
		return decimalPipe.transform(maxBid * factor, '1.0-3');
	}

	public static getErrorMessage(res: any): string[] {
		if (res) {
			if (res.graphQLErrors?.length) {
				return this.sanitizeErrorMessage(res.graphQLErrors[0].message);
			} else if (res.networkError?.body?.errors) {
				return this.sanitizeErrorMessage(res.networkError.body.errors.map(e => e.message).join('|'));
			} else if (res.networkError?.message) {
				return this.sanitizeErrorMessage(res.networkError.message);
			} else if (res.error?.message) {
				if (res.error.message?.response) {
					return this.sanitizeErrorMessage(res.error.message.response);
				} else {
					return this.sanitizeErrorMessage(typeof res.error.message === 'string' ? res.error.message : JSON.stringify(res.error.message));
				}
			} else if (res.message) {
				return this.sanitizeErrorMessage(res.message);
			}
		} else {
			return ['Unknown Error'];
		}
	}

	public static sanitizeErrorMessage(message: string): string[] {
		return message.includes('Unexpected token < in JSON')
			? ['The request failed. We have been notified and are working to fix the issue. Please check back again in 30 minutes.']
			: message
				.replace('Unexpected error value: ', '')
				.replace('Network error: Http failure response for (unknown url): 0 Unknown Error', 'Service Timeout')
				.replace(/\"/g, '')
				.replace('{ error: ', '')
				.replace('. }', '.')
				.split('|');
	}

	public static toFriendlyRate(rate: any): Rate {
		const friendlyRate = rate;
		friendlyRate.matrixPrice = rate.matrixPrice === undefined ? true : rate.matrixPrice;
		friendlyRate.displayRateRaw = RateUtils.getDisplayRateRaw(friendlyRate.displayRate);

		const outputRate = _.extend(friendlyRate, friendlyRate.extra ? JSON.parse(friendlyRate.extra) : {});

		outputRate.productDescription = outputRate.productDescription ? outputRate.productDescription : undefined;
		outputRate.productHeader = outputRate.productHeader ? outputRate.productHeader : undefined;
		outputRate.tdspKwhCharge = outputRate.tdspKwhCharge ? Number(outputRate.tdspKwhCharge) * 100 : undefined;
		outputRate.tdspBaseCharge = outputRate.tdspBaseCharge ? Number(outputRate.tdspBaseCharge).toFixed(2) : undefined;
		outputRate.baseFee1Dollars = outputRate.baseFee1Dollars ? Number(outputRate.baseFee1Dollars).toFixed(2) : undefined;
		outputRate.energyRate = outputRate.energyRate ? Number(outputRate.energyRate) * 100 : undefined;

		outputRate.supplierName = outputRate.name;
		if (!outputRate.planName) {
			outputRate.planName = outputRate.termName;
			if (!outputRate.planName) {
				outputRate.planName = outputRate.term + ' months';
			}
		}

		return outputRate;
	}

	public static customEvent(event: string, params?: any) {
		params = params || { bubbles: false, cancelable: false, detail: null };
		const evt = document.createEvent('CustomEvent');
		evt.initCustomEvent(event, params.bubbles, params.cancelable, params.detail);
		return evt;
	}

	public static getPath(path: string, withToken = false): string {
		const token = withToken ? '?access_token=' + environment.apiKey : '';
		return !path.includes('http') ? environment.baseUrl + path + token : path;
	}

	public static isLastDayOfMonth(date: Date): boolean {
		return new Date(new Date(date).setDate(new Date(date).getDate() + 1)).getDate() === 1;
	}

	public static isSameDate(date: Date, today: Date): boolean {
		return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
	}

	// is a tomorrow to b
	public static isTomorrow(a: Date, b: Date): boolean {
		const date1 = new Date(a);
		const date2 = new Date(b);

		// same month and year
		if (date1.getMonth() === date2.getMonth() && date1.getFullYear() === date2.getFullYear()) {
			if (date2.getDay() === 5) {
				date2.setDate(date2.getDate() + 2); // make it Sunday to check as business day
				return this.isTomorrow(date1, date2);
			}

			return date1.getDate() - date2.getDate() === 1;
		}

		// everything else, if we add one day it will
		// account for month difference and year difference
		date1.setDate(date1.getDate() + 1);
		date2.setDate(date2.getDate() + 1);
		if (date1.getMonth() === date2.getMonth() && date1.getFullYear() === date2.getFullYear()) {
			if (date2.getDay() === 5) {
				date2.setDate(date2.getDate() + 2); // make it Sunday to check as business day
				return this.isTomorrow(date1, date2);
			}

			return date1.getDate() - date2.getDate() === 1;
		}
		return false;
	}

	public static getSupplierNote(serviceTypeId: string, stateId: string, supplierId: string): string {
		if (
			CONSTANTS.states.texas === stateId &&
			[CONSTANTS.suppliers.sparkEnergy, CONSTANTS.suppliers.verdeEnergy, CONSTANTS.suppliers.providerPower, CONSTANTS.suppliers.majorEnergy].includes(
				supplierId,
			)
		) {
			return 'This supplier does not accept Priority Move In customers. If you need power ' + 'immediately, please select another supplier.';
		}

		if (
			CONSTANTS.states.illinois === stateId &&
			CONSTANTS.serviceTypes.electricity.serviceTypeId === serviceTypeId &&
			[CONSTANTS.suppliers.publicPower, CONSTANTS.suppliers.usge].includes(supplierId)
		) {
			return "Any account with an annual usage of less than 15,000 kWh may NOT be included in this supplier's contract.";
		}

		if (supplierId === CONSTANTS.suppliers.frontierUtilities) {
			return (
				'DISCLAIMER: Your actual price for electricity service may be different depending on how ' +
				'and when you use electricity. See the Electricity Facts Label for additional pricing information.'
			);
		}

		return '';
	}

	public static generateDateSelections(dates: Date[], step: 'day' | 'month' | 'year' = 'month'): any[] {
		return _.chain(dates)
			.groupBy(e => e.getFullYear())
			.toPairs()
			.map(g => {
				const year = g[1][0].getFullYear();
				const yearSelection: any = {
					key: year,
					text: year,
					value: year,
				};

				if (step === 'month' || step === 'day') {
					yearSelection.items = _.chain(g[1])
						.groupBy(g1 => g1.getMonth())
						.toPairs()
						.map(g1g => {
							const month = g1g[1][0].getMonth() + 1;
							const monthSelection: any = {
								key: moment.utc(g1g[1][0]).format('MMM'),
								text: moment.utc(g1g[1][0]).format('MMM'),
								value: `${year}/${month}`,
							};

							if (step === 'day') {
								monthSelection.items = g1g[1].map(d => ({
									key: moment.utc(d).format('DD'),
									text: moment.utc(d).format('DD'),
									value: `${year}/${month}/${d.getDate()}`,
								}));
							}

							return monthSelection;
						})
						.value();
				}

				return yearSelection;
			})
			.value();
	}

	// add any additional CMS-type users here, like Mike Grimes who just needs to edit
	// and maintain certain contracts that he deals with outside of our system flow
	public static isCMSUserForContract(user: User, contract: Contract): boolean {
		// admin can always view
		if (user.isAdmin || user.isSaas) {
			return true;
		}

		// brokers can only view if it's their contract or their sub agent's contract.
		if (contract.agentId !== user.agentId && contract.agent.parentId !== user.agentId) {
			return false;
		}

		if (user.agentId === CONSTANTS.agents.mikeGrimes) {
			return (
				[
					CONSTANTS.suppliers.santannaEnergy,
					CONSTANTS.suppliers.realgyEnergy,
					CONSTANTS.suppliers.vanguardEnergy,
					CONSTANTS.suppliers.nordicEnergy,
				].includes(contract.supplierId) || [CONSTANTS.states.missouri].includes(contract.stateId)
			);
		}

		return false;
	}

	public static isMikeGrimes(contract: Contract): boolean {
		if (contract.agentId === CONSTANTS.agents.mikeGrimes) {
			return (
				[
					CONSTANTS.suppliers.santannaEnergy,
					CONSTANTS.suppliers.realgyEnergy,
					CONSTANTS.suppliers.vanguardEnergy,
					CONSTANTS.suppliers.nordicEnergy,
				].includes(contract.supplierId) || [CONSTANTS.states.missouri].includes(contract.stateId)
			);
		}
	}

	public static isFileSize(files: File[] | File | string, maxMBSize: number): boolean {
		switch (files) {
			case 'null':
				files = null;
				break;
			case 'undefined':
				files = undefined;
				break;
		}

		if (typeof files === 'string') {
			return false;
		}
		if (!!files && (Array.isArray(files) ? files : [files]).some(f => NumberUtils.bytesToMegabytes(f.size) > maxMBSize)) {
			return false;
		}
		return true;
	}

	public static isFileType(files: File[] | string, expectedTypes: string): boolean {
		switch (files) {
			case 'null':
				files = null;
				break;
			case 'undefined':
				files = undefined;
				break;
		}

		if (!!files && (Array.isArray(files) ? files : [files]).some(f => !expectedTypes.split(',').includes(f.name.split('.').pop()))) {
			return false;
		}
		return true;
	}

	public static showAsteriskForBrokerFirstImpression(isNewBroker: boolean): boolean {
		const securityService = ServiceLocator.injector.get(SecurityService);
		return securityService.authFields?.loggedInUser?.isAdmin && isNewBroker;
	}

	public static showAsteriskForContractFirstImpression(contractStatus: number, isNewBrokerAuction: boolean): boolean {
		const securityService = ServiceLocator.injector.get(SecurityService);
		return securityService.authFields?.loggedInUser?.isAdmin && contractStatus === CONSTANTS.statuses.ainp && isNewBrokerAuction;
	}

	public static getEnergyIcon(serviceTypeId: string): string {
		if (serviceTypeId === CONSTANTS.serviceTypes.electricity.serviceTypeId) {
			return 'https://assets.powerkiosk.com/includes/images/electricity-icon.svg';
		} else if (serviceTypeId === CONSTANTS.serviceTypes.naturalGas.serviceTypeId) {
			return 'https://assets.powerkiosk.com/includes/images/gas-icon.svg';
		}

		return 'https://assets.powerkiosk.com/includes/images/electricity-icon.svg';
	}

	public static getUniqueId(length = 16) {
		return Math.ceil(Math.random() * Date.now())
			.toPrecision(length)
			.replace('.', '');
	}

	public static padShortZipCode(zipCode: string): string {
		if (zipCode?.length < 5 && zipCode?.length > 1) {
			return _.padStart(zipCode, 5, '0');
		}

		return zipCode;
	}

	// https://stackoverflow.com/a/10474055/924501
	public static getTotalMargin(
		input: Contract | ViewContractLogChanges | ReportContractStatus,
		includeBufferMargin = true,
		includeParentBrotherBufferMargin = false,
		includeMatrixAgentBufferMargin = false,
		unit?: string,
	): number {
		let totalMargin = input.margin;

		if (includeParentBrotherBufferMargin && (input.commissionSplitBufferMarginParent || input.commissionSplitBufferMarginBrother)) {
			totalMargin =
				Math.round((totalMargin + (input.commissionSplitBufferMarginParent || 0) + (input.commissionSplitBufferMarginBrother || 0)) * 1e12) / 1e12;
		}

		if (includeBufferMargin && input.bufferMargin) {
			totalMargin = Math.round((totalMargin + input.bufferMargin) * 1e12) / 1e12;
		}

		if (includeMatrixAgentBufferMargin && input.matrixAgentBufferMargin) {
			totalMargin = Math.round((totalMargin + input.matrixAgentBufferMargin) * 1e12) / 1e12;
		}

		totalMargin = NumberUtils.rateToUnits(totalMargin, unit);

		return totalMargin;
	}

	public static getTotalMarginFormatted(
		contract: Contract,
		includeBufferMargin = true,
		includeParentBrotherBufferMargin = false,
		includeMatrixAgentBufferMargin = false,
		marginUnits?: string,
		unit?: string,
	): string {
		const totalMargin = this.getTotalMargin(contract, includeBufferMargin, includeParentBrotherBufferMargin, includeMatrixAgentBufferMargin, unit);

		const currencyPipe = ServiceLocator.injector.get(CurrencyPipe);
		return `${currencyPipe.transform(totalMargin, 'USD', '', '1.2-5')} ${marginUnits} / ${unit}`;
	}

	public static getCommissionSplitBufferMarginParent(contract: Contract | ReportContractStatus, unit?: string): number {
		return NumberUtils.rateToUnits(contract.commissionSplitBufferMarginParent, unit);
	}

	public static getCommissionSplitBufferMarginBrother(contract: Contract | ReportContractStatus, unit?: string): number {
		return NumberUtils.rateToUnits(contract.commissionSplitBufferMarginBrother, unit);
	}

	public static getUsageVerification(input: UnpaidPayable | ViewPayableForecastDashboard | ReportCommissionProcessingIssuesRow): {
		accountUsage: {
			label: string;
			text: string;
			iconPrefix: string;
			icon: string;
			classes: string;
		};
		receivableUsage: {
			label: string;
			text: string;
			iconPrefix: string;
			icon: string;
			classes: string;
		};
	} {
		const decimalPipe = ServiceLocator.injector.get(DecimalPipe);

		// can view rule defintions and logic in getUsageVerification.sql table function
		switch (input.ruleId) {
			case 1:
				if (input.receivableUsageCheckId === UsageVerificationType.NOT_VERIFIED) {
					return {
						accountUsage: null,
						receivableUsage: {
							label: 'Actual Receivable Residual Mismatch',
							text: input.receivableUsage
								? `Actual Receivable Residual Mismatch: ` + `${decimalPipe.transform(input.receivableUsage, '1.0-0')} ${input.units}`
								: 'Actual Receivable Residual Missing',
							iconPrefix: 'fas',
							icon: 'circle-exclamation',
							classes: 'red-text',
						},
					};
				}
				break;
			case 2:
				const usageToCompare = input.accountUsage ?? input.confirmedAnnualUsage / 12;
				if (input.ruleTierId === 1 && input.accountUsageCheckId === UsageVerificationType.NOT_VERIFIED) {
					return {
						accountUsage: {
							label: 'Confirmed Annual Usage Mismatch',
							text: `${decimalPipe.transform(usageToCompare, '1.0-0')} ${input.units}`,
							iconPrefix: 'fas',
							icon: 'circle-exclamation',
							classes: 'red-text',
						},
						receivableUsage: null,
					};
				} else if (input.ruleTierId === 2 && input.receivableUsageCheckId === UsageVerificationType.NOT_VERIFIED) {
					return {
						accountUsage: null,
						receivableUsage: {
							label: 'Confirmed Residual Mismatch',
							text: `Confirmed Residual Mismatch: ${decimalPipe.transform(usageToCompare, '1.0-0')} ${input.units}`,
							iconPrefix: 'fas',
							icon: 'circle-exclamation',
							classes: 'red-text',
						},
					};
				}
				break;
			case 3:
				if (input.receivableUsageCheckId === UsageVerificationType.NOT_VERIFIED) {
					return {
						accountUsage: null,
						receivableUsage: {
							label: 'Confirmed Annual Usage Mismatch',
							text: `Confirmed Annual Usage Mismatch: ` + `${decimalPipe.transform(input.confirmedAnnualUsage || 0, '1.0-0')} ${input.units}`,
							iconPrefix: 'fas',
							icon: 'circle-exclamation',
							classes: 'red-text',
						},
					};
				}
				break;
			case 4:
				if (
					input.accountUsageCheckId === UsageVerificationType.MANUAL_VERIFICATION_NEEDED ||
					input.receivableUsageCheckId === UsageVerificationType.MANUAL_VERIFICATION_NEEDED
				) {
					return {
						accountUsage: {
							label: `Usage must be verified manually`,
							text: `Usage must be verified manually`,
							iconPrefix: 'far',
							icon: 'circle-exclamation',
							classes: 'orange-text',
						},
						receivableUsage: null, // even though it could be on the receivable usage side, let's not show a duplicate message
					};
				}
				break;
			case 5:
				if (input.receivableUsageCheckId === UsageVerificationType.NOT_VERIFIED) {
					return {
						accountUsage: null,
						receivableUsage: {
							label: 'Actual Receivable Upfront Mismatch',
							text: input.receivableUsage
								? `Actual Receivable Upfront Mismatch: ` + `${decimalPipe.transform(input.receivableUsage, '1.0-0')} ${input.units}`
								: 'Actual Receivable Upfront Missing',
							iconPrefix: 'fas',
							icon: 'circle-exclamation',
							classes: 'red-text',
						},
					};
				}
				break;
			case 6:
				if (input.receivableUsageCheckId === UsageVerificationType.NOT_VERIFIED) {
					return {
						accountUsage: null,
						receivableUsage: {
							label: 'Actual Receivable Residual Mismatch',
							text: input.receivableUsage
								? `Actual Receivable Residual Mismatch: ` + `${decimalPipe.transform(input.receivableUsage, '1.0-0')} ${input.units}`
								: 'Actual Receivable Residual Missing',
							iconPrefix: 'fas',
							icon: 'circle-exclamation',
							classes: 'red-text',
						},
					};
				}
				break;
			case 7:
				if (input.receivableUsageCheckId === UsageVerificationType.NOT_VERIFIED) {
					return {
						accountUsage: null,
						receivableUsage: {
							label: 'Actual Receivable Upfront Mismatch',
							text: input.receivableUsage
								? `Actual Receivable Upfront Mismatch: ` + `${decimalPipe.transform(input.receivableUsage, '1.0-0')} ${input.units}`
								: 'Actual Receivable Upfront Missing',
							iconPrefix: 'fas',
							icon: 'circle-exclamation',
							classes: 'red-text',
						},
					};
				}
				break;
			case 8:
				if (input.ruleTierId === 1 && input.receivableUsageCheckId === UsageVerificationType.NOT_VERIFIED) {
					return {
						accountUsage: null,
						receivableUsage: {
							label: 'Actual Receivable Residual Mismatch',
							text: input.receivableUsage
								? `Actual Receivable Residual Mismatch: ` + `${decimalPipe.transform(input.receivableUsage, '1.0-0')} ${input.units}`
								: 'Actual Receivable Residual Missing',
							iconPrefix: 'fas',
							icon: 'circle-exclamation',
							classes: 'red-text',
						},
					};
				} else if (input.ruleTierId === 2 && input.accountUsageCheckId === UsageVerificationType.NOT_VERIFIED) {
					return {
						accountUsage: {
							label: input.accountUsage && input.accountUsage !== 0 ? 'Utility Account Usage Mismatch' : 'Utility Account Usage Missing',
							text:
								input.accountUsage && input.accountUsage !== 0
									? `Utility Account Usage Mismatch: ${decimalPipe.transform(input.accountUsage, '1.0-0')} ${input.units}`
									: 'Utility Account Usage Missing',
							iconPrefix: 'fas',
							icon: 'circle-exclamation',
							classes: 'red-text',
						},
						receivableUsage: null,
					};
				}
				break;
			case 9: {
				if (input.receivableUsageCheckId === UsageVerificationType.NOT_VERIFIED) {
					return {
						accountUsage: null,
						receivableUsage: {
							label: 'Actual Receivable Missing',
							text: 'Actual Receivable Missing',
							iconPrefix: 'fas',
							icon: 'circle-exclamation',
							classes: 'red-text',
						},
					};
				}
				break;
			}
			case 12: {
				if (input.ruleTierId === 1 && input.receivableUsageCheckId === UsageVerificationType.NOT_VERIFIED) {
					return {
						accountUsage: null,
						receivableUsage: {
							label: 'Actual Receivable Upfront Mismatch',
							text: input.receivableUsage
								? `Actual Receivable Upfront Mismatch: ` + `${decimalPipe.transform(input.receivableUsage, '1.0-0')} ${input.units}`
								: 'Actual Receivable Upfront Missing',
							iconPrefix: 'fas',
							icon: 'circle-exclamation',
							classes: 'red-text',
						},
					};
				} else if (input.ruleTierId === 2 && input.receivableUsageCheckId === UsageVerificationType.NOT_VERIFIED) {
					return {
						accountUsage: null,
						receivableUsage: {
							label: 'Actual Receivable Residual Mismatch',
							text: input.receivableUsage
								? `Actual Receivable Residual Mismatch: ` + `${decimalPipe.transform(input.receivableUsage, '1.0-0')} ${input.units}`
								: 'Actual Receivable Residual Missing',
							iconPrefix: 'fas',
							icon: 'circle-exclamation',
							classes: 'red-text',
						},
					};
				}
				break;
			}
			case 13: {
				if (input.ruleTierId === 1 && input.receivableUsageCheckId === UsageVerificationType.NOT_VERIFIED) {
					return {
						accountUsage: null,
						receivableUsage: {
							label: 'There is unpaid payable',
							text: 'There is unpaid payable',
							iconPrefix: 'fas',
							icon: 'circle-exclamation',
							classes: 'blue-text',
						},
					};
				} else if (input.ruleTierId === 2 && input.receivableUsageCheckId === UsageVerificationType.NOT_VERIFIED) {
					return {
						accountUsage: null,
						receivableUsage: {
							label: `True-up mismatch receivable usage`,
							text: `True-up mismatch receivable usage. Receivable usage: ${decimalPipe.transform(input.receivableUsage, '1.0-0')} ${input.units}.`,
							iconPrefix: 'fas',
							icon: 'circle-exclamation',
							classes: 'red-text',
						},
					};
				} else if (input.ruleTierId === 3 && input.accountUsageCheckId === UsageVerificationType.NOT_VERIFIED) {
					return {
						accountUsage: null,
						receivableUsage: {
							label: `True-up mismatch utility account usage`,
							text: `True-up mismatch utility account usage. Account usage: ${decimalPipe.transform(input.accountUsage, '1.0-0')} ${input.units}.`,
							iconPrefix: 'fas',
							icon: 'circle-exclamation',
							classes: 'red-text',
						},
					};
				} else if (input.ruleTierId === 4 && input.receivableUsageCheckId === UsageVerificationType.NOT_VERIFIED) {
					return {
						accountUsage: null,
						receivableUsage: {
							label: `Total payable usage exceeds 30% of expected contract usage`,
							text: `Total payable usage exceeds 30% of expected contract usage`,
							iconPrefix: 'fas',
							icon: 'circle-exclamation',
							classes: 'red-text',
						},
					};
				}
				break;
			}
		}

		return { accountUsage: null, receivableUsage: null };
	}

	public parseBase64<T>(value: string): T {
		try {
			return JSON.parse(window.atob(value)) as T;
		} catch (e) {
			return null;
		}
	}

	public encBase64(value: any): string {
		try {
			return window.btoa(value);
		} catch (e) {
			return null;
		}
	}

	public isSSL(): boolean {
		return this.window.location.protocol === 'https:';
	}

	public getColumnFieldName(dataGrid: DxDataGrid, getter: any): string {
		if (this.isFunction(getter)) {
			const column = dataGrid.columnOption(getter.columnIndex);
			return column.dataField;
		} else {
			return getter;
		}
	}

	public getColumnFieldType(dataGrid: DxDataGrid, getter: any): string {
		if (this.isFunction(getter)) {
			const column = dataGrid.columnOption(getter.columnIndex);
			return column.dataType;
		} else if (this.isString(getter)) {
			const column = dataGrid.columnOption(getter.replace(/^!/, ''));
			if (column) {
				return column.dataType;
			}

			return null;
		}

		return null;
	}

	public isNullOrUndefined(value: any): boolean {
		return value === undefined || value === null;
	}

	public isFunction(variable: any): boolean {
		return variable instanceof Function;
	}

	public isString(variable: any): boolean {
		return typeof variable === 'string';
	}

	public buildFilterMap(dataGrid: DxDataGrid, filterValues: any, filterMapRoot?: any): any {
		let filterMap = filterMapRoot;
		if (filterValues) {
			// extract bang operator
			if (filterValues.length && filterValues[0] === '!') {
				if (Array.isArray(filterValues[1]) && Array.isArray(filterValues[1][0])) {
					for (const filterValue of filterValues[1]) {
						if (Array.isArray(filterValue)) {
							filterValue[0] = '!' + filterValue[0];
						}
					}
				} else {
					filterValues[1][0] = '!' + filterValues[1][0];
				}
				filterValues.shift();
			}
			// organize into filter map
			for (let i = 0; i < filterValues.length; i++) {
				if (i === 0 && (this.isString(filterValues[i]) || this.isFunction(filterValues[i]))) {
					const columnFieldName = this.getColumnFieldName(dataGrid, filterValues[0]);
					const columnFieldNameOpposite = '!' + columnFieldName;
					const columnFieldType = this.getColumnFieldType(dataGrid, columnFieldName);
					if (columnFieldName.includes('Date') && columnFieldType === 'date') {
						if (!filterMap[columnFieldName]) {
							filterMap[columnFieldName] = [];
						}
						if (filterValues[1] === '>=') {
							filterMap[columnFieldName].push([filterValues]);
						} else {
							filterMap[columnFieldName][filterMap[columnFieldName].length - 1].push(filterValues);
						}
					} else if (!this.isNullOrUndefined(filterMap[columnFieldNameOpposite])) {
						if (Array.isArray(filterMap[columnFieldNameOpposite])) {
							filterMap[columnFieldNameOpposite].push(!isNaN(filterValues[2]) ? filterValues[2] : StringUtils.toBoolean(`${filterValues[2]}`));
						} else {
							filterMap[columnFieldNameOpposite] = [filterMap[columnFieldNameOpposite], filterValues[2]];
						}
					} else if (!this.isNullOrUndefined(filterMap[columnFieldName])) {
						if (Array.isArray(filterMap[columnFieldName])) {
							filterMap[columnFieldName].push(!isNaN(filterValues[2]) ? filterValues[2] : StringUtils.toBoolean(`${filterValues[2]}`));
						} else {
							filterMap[columnFieldName] = [filterMap[columnFieldName], filterValues[2]];
						}
					} else {
						if (Array.isArray(filterValues[2])) {
							filterMap[columnFieldName] = !isNaN(filterValues[2][2]) ? filterValues[2][2] : StringUtils.toBoolean(`${filterValues[2][2]}`);
						} else {
							filterMap[columnFieldName] = !isNaN(filterValues[2]) ? filterValues[2] : StringUtils.toBoolean(`${filterValues[2]}`);
						}
					}
				} else if (Array.isArray(filterValues[i])) {
					filterMap = this.buildFilterMap(dataGrid, filterValues[i], filterMap);
				}
			}

			return filterMap;
		} else {
			return filterMap;
		}
	}

	public convertFromStringToPrimitive(value: string): any {
		if (value === 'null') {
			return null;
		}
		if (value === 'undefined') {
			return undefined;
		}
		return value;
	}

	public removeFileFromEventTarget(event: EventTarget): void {
		const eventObj = event as any;
		const target = eventObj.target as HTMLInputElement;
		target.value = '';
	}

	public getFilesFromEventTarget(event: EventTarget): FileList {
		const eventObj = event as any;
		const target = eventObj.target as HTMLInputElement;
		return target.files;
	}

	public getLanguages(): { value: string; text: string }[] {
		return [
			{ value: 'english', text: 'English' },
			{ value: 'spanish', text: 'Spanish' },
		];
	}

	public getDwellingTypes(stateId: string, isResidential: boolean): string[] {
		if (isResidential && stateId === CONSTANTS.states.texas) {
			return ['Apartment', 'House'];
		}

		return [];
	}

	public sanitizeUtilityAccountNumber(utilityAccountNum: string, isAGL: boolean, isResidential: boolean, switchOption: string): string {
		const isMoveIn = switchOption?.toLowerCase().includes('move');

		if (!utilityAccountNum || (isAGL && isResidential && isMoveIn)) {
			return '';
		}

		let formattedAccountNumber = utilityAccountNum;
		formattedAccountNumber = formattedAccountNumber.replace(/[-\.,]/g, '');

		if (isAGL) {
			while (formattedAccountNumber.length < 10) {
				formattedAccountNumber = '0' + formattedAccountNumber;
			}
		}

		return formattedAccountNumber;
	}

	public formatMeterNum(meterNum: string, isNipscoResidentialGas: boolean): string {
		if (meterNum) {
			let trimmedMeterNum = meterNum.trim();

			if (trimmedMeterNum) {
				// NIPSCO Residential Gas requires meter number to be padded with 0, max length is 8
				if (isNipscoResidentialGas) {
					trimmedMeterNum = trimmedMeterNum.padEnd(8, '0');
				}
			}

			return trimmedMeterNum;
		}

		return '';
	}

	public getDateFromBusinessTimeAgo(
		targetAmount: number,
		unit: moment.unitOfTime.StartOf | moment.unitOfTime.DurationConstructor,
		targetDate?: string,
	): moment.Moment {
		const startDate = moment(targetDate ?? undefined).startOf(unit);

		let counter = 0;
		do {
			while ([6, 7].includes(startDate.isoWeekday())) {
				startDate.subtract(1, unit as moment.unitOfTime.DurationConstructor);
			}
			startDate.subtract(1, unit as moment.unitOfTime.DurationConstructor);
			counter += 1;
		} while (counter < targetAmount);

		return startDate;
	}

	public generateDatesList(start: Date, end: Date, step: 'day' | 'month' | 'year' = 'day'): Date[] {
		const list = [];
		if (start <= end) {
			for (const date = start; date.valueOf() <= end.valueOf(); this.dateListStep(date, step, 1)) {
				list.push(new Date(date));
			}
		} else {
			for (const date = start; date.valueOf() >= end.valueOf(); this.dateListStep(date, step, -1)) {
				list.push(new Date(date));
			}
		}

		return list;
	}

	private dateListStep(date: Date, step: 'day' | 'month' | 'year', direction: number): void {
		switch (step) {
			case 'day': {
				date.setDate(date.getDate() + direction * 1);
				break;
			}
			case 'month': {
				date.setMonth(date.getMonth() + direction * 1);
				break;
			}
			case 'year': {
				date.setFullYear(date.getFullYear() + direction * 1);
				break;
			}
		}
	}

	public generateNumberList(start: number, end: number, step = 1): number[] {
		const list = [];
		if (start <= end) {
			for (let i = start; i <= end; i += step) {
				list.push(i);
			}
		} else {
			for (let i = start; i >= end; i -= step) {
				list.push(i);
			}
		}

		return list;
	}

	public generateYearsList(start: Date, end: Date, step = 1): Date[] {
		const list = [];
		let current = new Date(start);
		do {
			list.push(current);
			current = new Date(start.setFullYear(start.getFullYear() + step));
		} while (start.getTime() <= end.getTime());

		return list;
	}

	public generateTimesList(start: Date, end: Date, step = 60): Date[] {
		const list = [];
		let current = new Date(start);
		do {
			list.push(current);
			current = new Date(start.setMinutes(start.getMinutes() + step));
		} while (start.getTime() <= end.getTime());

		return list;
	}

	public generateTimesListFromMoment(start: moment.Moment, end: moment.Moment, step = 60): moment.Moment[] {
		const list = [];
		const current = start.clone();
		do {
			list.push(current.clone());
			current.add(step, 'minutes');
		} while (current.unix() <= end.unix());

		return list;
	}

	public getMonths() {
		return ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
	}

	public convertArrayToObjectMap(arr: any[]): any {
		const obj = {} as any;
		arr.forEach(item => {
			obj[item] = true;
		});
		return obj;
	}

	public isZipCode(value: any, country: Country): boolean {
		return StringUtils.matches(String(value), country.zipCodeFormat);
	}

	public getErrorMessage(res: any): string[] {
		return HelperService.getErrorMessage(res);
	}

	public getPath(path: string): string {
		return HelperService.getPath(path);
	}

	public sanitizeApolloError(message: string): string {
		return message.replace(/Network error:\s+/g, '').replace(/Unexpected error value:\s+/g, '');
	}

	public isLastDayOfMonth(date: Date): boolean {
		return HelperService.isLastDayOfMonth(date);
	}

	public isSameDate(date: Date, today: Date): boolean {
		return HelperService.isSameDate(date, today);
	}

	public getProductMinBidAmount(productId: string, serviceTypeId: string, units: string): string {
		return HelperService.getProductMinBidAmount(productId, serviceTypeId, units);
	}

	public getProductMaxBidAmount(productId: string, serviceTypeId: string, stateId: string, units: string): string {
		return HelperService.getProductMaxBidAmount(productId, serviceTypeId, stateId, units);
	}

	public s2ab(s: string): ArrayBuffer {
		const buf = new ArrayBuffer(s.length);
		const view = new Uint8Array(buf);
		// eslint-disable-next-line no-bitwise
		for (let i = 0; i !== s.length; ++i) {
			view[i] = s.charCodeAt(i) & 0xff;
		}
		return buf;
	}

	// add any additional CMS-type users here, like Mike Grimes who just needs to edit
	// and maintain certain contracts that he deals with outside of our system flow
	public isCMSUserForContract(user: User, contract: Contract): boolean {
		return HelperService.isCMSUserForContract(user, contract);
	}

	public isMikeGrimes(contract: Contract): boolean {
		return HelperService.isMikeGrimes(contract);
	}

	public generateDateSelections(dates: Date[], step: 'day' | 'month' | 'year' = 'month'): any[] {
		return HelperService.generateDateSelections(dates, step);
	}

	public isFileSize(files: string | File[], maxMBSize: number): boolean {
		return HelperService.isFileSize(files, maxMBSize);
	}

	public getSupplierNote(serviceTypeId: string, stateId: string, supplierId: string): string {
		return HelperService.getSupplierNote(serviceTypeId, stateId, supplierId);
	}

	/**
	 * Builds a Lucene query for AWS CloudSearch agent index: exact phrase and partial matches with per-field boost.
	 * Ranking: name > parent_name > contact_name > email > agent_id. Fuzzy layer matches contract search behavior.
	 *
	 * CloudSearch sometimes indexes "&" as the literal characters "a m p ;" after "&" (i.e. HTML entity text). We OR phrase
	 * variants (real "&" and "&amp;") so DB-accurate queries still match the index.
	 */
	public getAgentSearchQuery(options: { search: string; includeParent?: boolean; defaultSearch?: string }): string {
		const rawSearch = (options.search || '').trim();
		const sanitizedSearch = this.sanitizeAdvancedSearchParameter(rawSearch).trim();

		if (!sanitizedSearch) {
			return options.defaultSearch;
		}

		// Searchable fields with boost: name highest, then parent_name, contact_name, email, agent_id
		const rankedFields: { field: string; boost: number }[] = [
			{ field: 'name', boost: 6 },
			{ field: 'parent_name', boost: 5 },
			{ field: 'contact_name', boost: 4 },
			{ field: 'email', boost: 3 },
			{ field: 'agent_id', boost: 2 },
		];

		const tokens = rawSearch.split(/\s+/).filter(t => t.length);
		const significantTokens = tokens.filter(t => t.length >= 2);
		const agentSearchStopWords = new Set([
			'the',
			'a',
			'an',
			'and',
			'or',
			'but',
			'in',
			'on',
			'at',
			'to',
			'for',
			'of',
			'with',
			'by',
			'from',
			'as',
			'is',
			'was',
			'are',
			'were',
			'been',
			'be',
			'have',
			'has',
			'had',
			'do',
			'does',
			'did',
		]);
		const companyDesignations = new Set(['llc', 'inc', 'l.l.c.', 'inc.', 'dba', 'd.b.a.']);
		const searchableTokens = significantTokens.filter(
			t => !agentSearchStopWords.has(t.toLowerCase()) && !companyDesignations.has(t.toLowerCase()),
		);

		// 1) Exact phrase match per field with ranking boost. Use pre-sanitize text for variants: sanitize() turns & into \&,
		// so we must not test sanitizedSearch.includes('&'). Match CloudSearch literal "&amp;" text and "&"-less token streams.
		const phraseVariants = new Set<string>();
		const pushSanitizedPhrase = (raw: string) => {
			const s = this.sanitizeAdvancedSearchParameter(raw).trim();
			if (s) {
				phraseVariants.add(s);
			}
		};
		pushSanitizedPhrase(rawSearch);
		if (rawSearch.includes('&')) {
			pushSanitizedPhrase(rawSearch.replace(/&/g, '&amp;'));
			const collapsed = rawSearch.replace(/&+/g, ' ').replace(/\s+/g, ' ').trim();
			pushSanitizedPhrase(collapsed);
		}
		const exactPhraseClauses: string[] = [];
		for (const phrase of phraseVariants) {
			for (const { field, boost } of rankedFields) {
				exactPhraseClauses.push(`${field}:"${phrase}"^${boost}`);
			}
		}
		const exactPhrase = `(${exactPhraseClauses.join(' OR ')})`;

		// 2) Partial/prefix: only searchable tokens (no stop words) to avoid matching on "the", "of", etc.
		const prefixClauses: string[] = [];
		for (const token of searchableTokens) {
			const sanitizedToken = this.sanitizeAdvancedSearchParameter(token);
			for (const { field, boost } of rankedFields) {
				prefixClauses.push(`${field}:${sanitizedToken}*^${boost}`);
			}
			if (token.includes('&')) {
				const entityToken = token.replace(/&/g, '&amp;');
				const sanitizedEntityToken = this.sanitizeAdvancedSearchParameter(entityToken);
				for (const { field, boost } of rankedFields) {
					prefixClauses.push(`${field}:${sanitizedEntityToken}*^${boost}`);
				}
			}
		}
		const partialMatch = prefixClauses.length ? `(${prefixClauses.join(' OR ')})` : '';

		// 3) Fuzzy per searchable token with per-field boost (stop words excluded)
		const fuzzyClauses: string[] = [];
		for (const token of searchableTokens) {
			if (token.length >= 2) {
				const sanitizedToken = this.sanitizeAdvancedSearchParameter(token);
				for (const { field, boost } of rankedFields) {
					fuzzyClauses.push(`${field}:${sanitizedToken}~1^${boost}`);
				}
				fuzzyClauses.push(`${sanitizedToken}~1`);
				if (token.includes('&')) {
					const sanitizedEntityToken = this.sanitizeAdvancedSearchParameter(token.replace(/&/g, '&amp;'));
					for (const { field, boost } of rankedFields) {
						fuzzyClauses.push(`${field}:${sanitizedEntityToken}~1^${boost}`);
					}
				}
				if (token.includes('@')) {
					fuzzyClauses.push(`email:${sanitizedToken}~1^3`);
					const localPart = token.split('@')[0];
					if (localPart.length >= 2) {
						fuzzyClauses.push(`email:${this.sanitizeAdvancedSearchParameter(localPart)}~1^3`);
					}
				}
			}
		}
		const fuzzyMatch = fuzzyClauses.length ? `(${fuzzyClauses.join(' OR ')})^0.7` : '';

		const parts = [exactPhrase];
		if (partialMatch) parts.push(partialMatch);
		if (fuzzyMatch) parts.push(fuzzyMatch);

		return parts.join(' OR ');
	}

	/**
	 * Builds a single Lucene query for AWS CloudSearch that combines exact phrase,
	 * partial (prefix) matches, and optional fuzzy matching for Google-like smart search.
	 * Exact and phrase matches rank higher via boost; fuzzy handles typos.
	 */
	public getContractSearchQuery(options: { search: string; dbaName: string; utilityAccountNum: string }): string {
		let search = '';

		if (options.search) {
			const sanitizedSearch = this.sanitizeAdvancedSearchParameter(options.search).trim();
			if (!sanitizedSearch) {
				return search;
			}

			// Searchable fields with boost: contract_num > customer_dba_name > customer_email > location_utility_account_num
			// > location_address
			const rankedFields: { field: string; boost: number }[] = [
				{ field: 'contract_num', boost: 6 },
				{ field: 'customer_dba_name', boost: 5 },
				{ field: 'customer_email', boost: 4 },
				{ field: 'location_utility_account_num', boost: 3 },
				{ field: 'location_address', boost: 2 },
				{ field: 'location_address_line1', boost: 2 },
				{ field: 'customer_contact_name', boost: 1 },
			];

			const tokens = sanitizedSearch.split(/\s+/).filter(t => t.length);
			// Skip single chars/digits for partial and fuzzy to avoid matching thousands of records (e.g. "D", "7")
			const significantTokens = tokens.filter(t => t.length >= 2);
			// Exclude common stop words from partial/fuzzy so "The University of Chicago" doesn't match on "the"/"of"
			const contractSearchStopWords = new Set([
				'the',
				'a',
				'an',
				'and',
				'or',
				'but',
				'in',
				'on',
				'at',
				'to',
				'for',
				'of',
				'with',
				'by',
				'from',
				'as',
				'is',
				'was',
				'are',
				'were',
				'been',
				'be',
				'have',
				'has',
				'had',
				'do',
				'does',
				'did',
			]);
			// Exclude common company designations like LLC, INC, etc. so "K&G TX231 LLC DBA Sport Clips" doesn't match on "llc" or "dba"
			const companyDesignations = new Set(['llc', 'inc', 'l.l.c.', 'inc.', 'dba', 'd.b.a.']);
			const searchableTokens = significantTokens.filter(
				t => !contractSearchStopWords.has(t.toLowerCase()) && !companyDesignations.has(t.toLowerCase()),
			);

			// 1) Exact phrase match per field with ranking boost (contract_num highest, then dba, email, utility, address, billing)
			const exactPhraseClauses = rankedFields.map(({ field, boost }) => `${field}:"${sanitizedSearch}"^${boost}`);
			const exactPhrase = `(${exactPhraseClauses.join(' OR ')})`;

			// 1a) contract_num partial match for text (tokenized) field: CloudSearch tokenizes on hyphen, so
			// "IL0000018617-00" is terms ["IL0000018617", "00"]. Query "IL0000018617-0" must use prefix per segment:
			// (contract_num:IL0000018617* AND contract_num:0*) so it matches "IL0000018617-00", "IL0000018617-01", etc.
			// Guard: contract numbers never contain spaces, so skip this clause for any multi-word input (e.g. a company
			// name like "F AND F KEY AND LOCK") — those are handled by the exact phrase and partial/fuzzy rules instead.
			// Also filter Lucene reserved keywords (case-sensitive: AND, OR, NOT…) from individual hyphen-delimited
			// segments so they aren't interpreted as boolean operators in unquoted prefix terms.
			const luceneReservedKeywords = new Set(['AND', 'OR', 'NOT', 'TO', 'NULL']);
			let contractNumPartialMatch = '';
			if (sanitizedSearch.length >= 2 && !/\s/.test(sanitizedSearch)) {
				const segments = sanitizedSearch.split(/-/).filter(s => s.length);
				const safeSegments = segments.filter(s => !luceneReservedKeywords.has(s));
				if (safeSegments.length === 1) {
					contractNumPartialMatch = `contract_num:${safeSegments[0]}*^6`;
				} else if (safeSegments.length > 1) {
					const prefixClauses = safeSegments.map(seg => `contract_num:${seg}*`);
					contractNumPartialMatch = `(${prefixClauses.join(' AND ')})^6`;
				}
			}

			// 1b) Address phrase boost: phrase in address fields ranks higher; location before billing per ranking.
			const q = (f: string, b: number) => `${f}:"${sanitizedSearch}"^${b}`;
			const addressPhraseBoost =
				tokens.length >= 2
					? `(${q('location_address_line1', 2)} OR ${q('location_address', 2)} OR ${q('customer_billing_address_line1', 1)} OR ${q('customer_billing_address', 1)})`
					: '';

			// 2) Partial/prefix: only searchable tokens (no stop words) to avoid matching on "the", "of", etc.
			const prefixClauses: string[] = [];
			for (const token of searchableTokens) {
				for (const { field, boost } of rankedFields) {
					prefixClauses.push(`${field}:${token}*^${boost}`);
				}
			}
			const partialMatch = prefixClauses.length ? `(${prefixClauses.join(' OR ')})` : '';

			// 3) Fuzzy per searchable token with per-field boost (stop words excluded)
			const fuzzyClauses: string[] = [];
			for (const token of searchableTokens) {
				if (token.length >= 2) {
					for (const { field, boost } of rankedFields) {
						fuzzyClauses.push(`${field}:${token}~1^${boost}`);
					}
					// Email-like: keep unqualified fuzzy for backwards compatibility
					fuzzyClauses.push(`${token}~1`);
					if (token.includes('@')) {
						fuzzyClauses.push(`customer_email:${token}~1^4`);
						const localPart = token.split('@')[0];
						if (localPart.length >= 2) {
							fuzzyClauses.push(`customer_email:${localPart}~1^4`);
						}
					}
				}
			}
			const fuzzyMatch = fuzzyClauses.length ? `(${fuzzyClauses.join(' OR ')})^0.7` : '';

			const parts = [exactPhrase];
			if (contractNumPartialMatch) parts.push(contractNumPartialMatch);
			if (addressPhraseBoost) parts.push(addressPhraseBoost);
			if (partialMatch) parts.push(partialMatch);
			if (fuzzyMatch) parts.push(fuzzyMatch);
			search = parts.join(' OR ');
		} else {
			if (options.dbaName) {
				const dbaName = options.dbaName || '';
				const sanitizedDbaName = this.sanitizeAdvancedSearchParameter(dbaName.replace(/'/g, ''));

				const exactMatchDbaName = `"${sanitizedDbaName}"`;
				const containsMatchDbaName = `*${sanitizedDbaName.split(' ').join('* *')}*`;

				search += `customer_dba_name:(${containsMatchDbaName}) OR customer_dba_name:(${exactMatchDbaName})`;
				search += ` OR customer_dba_name2:(${containsMatchDbaName}) OR customer_dba_name2:(${exactMatchDbaName})`;
			}

			if (options.utilityAccountNum) {
				const utilityAccountNum = options.utilityAccountNum || '';
				const sanitizedUtilityAccountNum = this.sanitizeAdvancedSearchParameter(utilityAccountNum.replace(/-/g, ''));

				const exactMatchUtilityAccountNum = `"${sanitizedUtilityAccountNum}"`;
				const containsMatchUtilityAccountNum = `*${sanitizedUtilityAccountNum.split(' ').join('* *')}*`;

				if (options.dbaName) {
					search += ` OR `;
				}

				search +=
					`location_utility_account_num:(${containsMatchUtilityAccountNum})` + ` OR location_utility_account_num:(${exactMatchUtilityAccountNum})`;
				search +=
					` OR location_utility_account_num2:(${containsMatchUtilityAccountNum})` +
					` OR location_utility_account_num2:(${exactMatchUtilityAccountNum})`;
			}
		}

		return search;
	}

	/**
	 * Escapes Lucene special characters so the query is treated literally.
	 * Literal "AND" and "OR" are left as-is so they can match in DBA names etc.
	 */
	public sanitizeAdvancedSearchParameter(query: string): string {
		// `&&` first so boolean AND is escaped as a unit; single `&` (e.g. "AT&T", "Foo & Bar") is escaped for Lucene.
		return query.trim().replace(/(&&|\|\||\\|[+\-!(){}[\]^"~*?:/&])/g, (m: string) => (m === '\\' ? '\\\\' : '\\' + m));
	}

	public showAsteriskForBrokerFirstImpression(isNewBroker: boolean): boolean {
		return HelperService.showAsteriskForBrokerFirstImpression(isNewBroker);
	}

	public showAsteriskForContractFirstImpression(contractStatus: number, isNewBrokerAuction: boolean): boolean {
		return HelperService.showAsteriskForContractFirstImpression(contractStatus, isNewBrokerAuction);
	}

	public getEnergyIcon(serviceTypeId: string): string {
		return HelperService.getEnergyIcon(serviceTypeId);
	}

	public padShortZipCode(zipCode: string): string {
		return HelperService.padShortZipCode(zipCode);
	}

	public getTotalMargin(
		contract: Contract,
		includeBufferMargin = true,
		includeParentBufferMargin = false,
		includeMatrixAgentBufferMargin = false,
		unit?: string,
	): number {
		return HelperService.getTotalMargin(contract, includeBufferMargin, includeParentBufferMargin, includeMatrixAgentBufferMargin, unit);
	}

	public getTotalMarginFormatted(
		contract: Contract,
		includeBufferMargin = true,
		includeParentBufferMargin = false,
		includeMatrixAgentBufferMargin = false,
		marginUnits?: string,
		unit?: string,
	): string {
		return HelperService.getTotalMarginFormatted(
			contract,
			includeBufferMargin,
			includeParentBufferMargin,
			includeMatrixAgentBufferMargin,
			marginUnits,
			unit,
		);
	}

	public getCommissionSplitBufferMarginParent(contract: Contract, unit?: string): number {
		return HelperService.getCommissionSplitBufferMarginParent(contract, unit);
	}

	public getCommissionSplitBufferMarginBrother(contract: Contract, unit?: string): number {
		return HelperService.getCommissionSplitBufferMarginBrother(contract, unit);
	}

	/**
	 * @returns A loader function to be used in a DxDataGrid's CustomStore. The loader function will use the output of `func`
	 * as the data. However, if the loader function is called less than `delay` milliseconds after the previous call and
	 * the previous call hasn't resolved yet, the previous call's request will be canceled and its promise will instead be
	 * resolved with the latest call's data instead.
	 */
	public throttledLoader<D>(
		func: (loadOptions: LoadOptions) => Observable<{ data: D[]; totalCount: number; summary?: any[] }>,
		delay = 300,
	): (loadOptions: LoadOptions) => Promise<{ data: D[]; totalCount: number; summary?: any[] }> {
		let pendingResolvers: ((data: { data: D[]; totalCount: number; summary?: number[] }) => void)[] = [];
		let latestData = { data: [] as D[], totalCount: 0, summary: [] as any[] };

		const subject = new Subject<LoadOptions>();
		subject
			.pipe(
				throttleTime(delay, undefined, { leading: true, trailing: true }),
				switchMap(loadOptions => func(loadOptions)),
			)
			.subscribe(data => {
				pendingResolvers.forEach(resolve => resolve(data));
				pendingResolvers = [];
			});

		return async (loadOptions: LoadOptions) => {
			if (loadOptions['dataField']) {
				return Promise.resolve(latestData);
			}

			const resultPromise = new Promise<{ data: D[]; totalCount: number; summary?: any[] }>(resolve => pendingResolvers.push(resolve));
			subject.next(loadOptions);
			const result = await resultPromise;

			latestData = {
				data: result.data ?? [],
				totalCount: result.totalCount,
				summary: result.summary ?? [],
			};
			return latestData;
		};
	}

	public getExcelColRange(colLength: number): string {
		let endCol: string;
		if (colLength <= 26) {
			endCol = String.fromCharCode('A'.charCodeAt(0) + colLength);
		} else {
			endCol = String.fromCharCode('A'.charCodeAt(0) + colLength / 26 - 1) + String.fromCharCode('A'.charCodeAt(0) + (colLength % 26));
		}
		return 'A1:' + endCol + '1';
	}

	public async getExcelSheetNames(file: File) {
		if (!file) {
			return [];
		}

		const sheetNames: string[] = await new Promise(async (resolve, reject) => {
			const reader = new FileReader();
			reader.onload = (ev: any) => {
				try {
					const fileResult = new Uint8Array(ev.target.result);
					const workbook = XLSX.read(fileResult, { type: 'array' });
					resolve(workbook.SheetNames);
				} catch (e) {
					reject(e);
				}
			};
			reader.readAsArrayBuffer(file);
		});

		return sheetNames;
	}

	public getUsageVerification(input: UnpaidPayable | ViewPayableForecastDashboard): {
		accountUsage: {
			text: string;
			iconPrefix: string;
			icon: string;
			classes: string;
		};
		receivableUsage: {
			text: string;
			iconPrefix: string;
			icon: string;
			classes: string;
		};
	} {
		return HelperService.getUsageVerification(input);
	}

	public static getPriceByProduct(rates: ContractMarketPerformance, productId: string, isTexas: boolean): number {
		let rate = null;

		if (!rates) {
			return rate;
		}

		switch (productId) {
			case CONSTANTS.products.indexPlus:
				rate = rates.energy;
				break;
			case CONSTANTS.products.fixedWithCapacityPassThrough:
				rate = rates.capacity;
				break;
			case CONSTANTS.products.congestionPassThrough:
				rate = rates.congestion;
				break;
			case CONSTANTS.products.energyOnly:
				rate =
					rates.ancillary !== null && (isTexas ? rates.congestion : rates.capacity) !== null
						? rates.ancillary + (isTexas ? rates.congestion : rates.capacity)
						: null;
				break;
			case CONSTANTS.products.supplierFeeOnlyAllComponentsPassThrough:
				rate =
					rates.energy !== null && rates.ancillary !== null && (isTexas ? rates.congestion : rates.capacity) !== null
						? rates.energy + rates.ancillary + (isTexas ? rates.congestion : rates.capacity)
						: null;
				break;
			case CONSTANTS.products.indexPlusExcludesCapacityEnergy:
				rate = rates.energy !== null && rates.capacity !== null ? rates.energy + rates.capacity : null;
				break;
			case CONSTANTS.products.nymexPlus:
				rate = rates.nymex;
				break;
			case CONSTANTS.products.indexAdderLocalHub:
			case CONSTANTS.products.nymexAdder:
				rate = rates.nymex !== null && rates.basis !== null ? rates.nymex + rates.basis : null;
				break;
			default:
				//To distinguish between not having a component vs fixed all in products
				rate = 0;
				break;
		}

		return rate;
	}

	public openAboutLayeredPurchasingModal() {
		this.modalService.show(FeatureTourModalComponent, {
			class: 'pk-modal modal-lg',
			initialState: {
				featureTourLocationId: CONSTANTS.featureTourLocations.aboutLayeredPurchasing,
			},
		});
	}

	public showAboutLayeredPurchasingIcon(contract: Contract) {
		const user = new User(this.securityService.authFields?.loggedInUser);
		return !user.isSaas && user?.agent && !user?.agent?.parentId && contract.fitsAboutLayeredPurchasingReq;
	}

	public aboutLayeredPurchasingToolTip() {
		return 'Have you considered a layered purchasing strategy?';
	}

	public getUniqueName(names: { [key: string]: number }, name: string, maxLength?: number): string {
		if (names[name]) {
			const counter = names[name];
			const counterWithName = ` (${counter})`;
			if (maxLength && (name + counterWithName).length > maxLength) {
				name = name.slice(0, name.length - ((name + counterWithName).length - maxLength));
				if (!names[name]) {
					names[name] = 1;
					return name;
				}

				names[name] = counter + 1;
				return `${name}${counterWithName}`;
			}

			names[name] = counter + 1;
			return `${name}${counterWithName}`;
		} else if (maxLength) {
			if (name.length > maxLength) {
				name = name.slice(0, name.length - (name.length - maxLength));
				return this.getUniqueName(names, name, maxLength);
			}
		}

		names[name] = 1;
		return name;
	}

	public maskedTaxIdOptions = (type: 'ssn' | 'fein'): MaskitoOptions => {
		return {
			// Allows either raw digits (fresh/edited input) or the server-masked format (***-**-XXXX/**-***XXXX).
			mask: type === 'ssn'
				? [/\d|\*/, /\d|\*/, /\d|\*/, '-', /\d|\*/, /\d|\*/, '-', /\d/, /\d/, /\d/, /\d/]
				: [/\d|\*/, /\d|\*/, '-', /\d|\*/, /\d|\*/, /\d|\*/, /\d/, /\d/, /\d/, /\d/],
			preprocessors: [
				({ elementState: { value, selection }, data }) => {
					// If the value has asterisks but no longer matches the intact masked format, the user
					// is trying to edit a previously saved value. Clear the field so they can type fresh
					// digits with no masking. The taxId/ssn form control value itself will be the real value.
					if (value.includes('*') && !(type === 'ssn' ? /^\*\*\*-\*\*-\d{4}$/.test(value) : /^\*\*-\*\*\*\d{4}$/.test(value))) {
						return { elementState: { value: '', selection: [0, 0] as [number, number] } };
					}
					return { elementState: { value, selection }, data };
				},
			],
		};
	};
}
